import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { api } from "../lib/api";
import { toast } from "sonner";
import {
  Lock, Plus, Pencil, Trash2, ArrowLeft, Save, X, Eye
} from "lucide-react";
import { Link } from "react-router-dom";

export default function AdminBlog() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [password, setPassword] = useState("");
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editingPost, setEditingPost] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({
    slug: "",
    title: "",
    excerpt: "",
    content: "",
    category: "",
    author: "",
    readTime: "",
    image: ""
  });

  const categories = ["Immigration", "Study Abroad", "IELTS", "Work Permits", "Investor Visa", "Tourist Visa"];

  useEffect(() => {
    if (isAuthenticated) {
      loadPosts();
    }
  }, [isAuthenticated]);

  const loadPosts = async () => {
    setLoading(true);
    try {
      const data = await api.getBlogPosts();
      setPosts(data);
    } catch (err) {
      toast.error("Failed to load blog posts");
    }
    setLoading(false);
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    try {
      await api.verifyAdmin(password);
      setIsAuthenticated(true);
      toast.success("Welcome to the admin panel!");
    } catch (err) {
      toast.error("Invalid password");
    }
  };

  const resetForm = () => {
    setFormData({
      slug: "",
      title: "",
      excerpt: "",
      content: "",
      category: "",
      author: "",
      readTime: "",
      image: ""
    });
    setEditingPost(null);
    setShowForm(false);
  };

  const handleEdit = (post) => {
    setFormData({
      slug: post.slug,
      title: post.title,
      excerpt: post.excerpt,
      content: post.content,
      category: post.category,
      author: post.author,
      readTime: post.readTime,
      image: post.image
    });
    setEditingPost(post);
    setShowForm(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      if (editingPost) {
        const { slug, ...updateData } = formData;
        await api.updateBlogPost(editingPost.slug, updateData, password);
        toast.success("Blog post updated successfully!");
      } else {
        await api.createBlogPost(formData, password);
        toast.success("Blog post created successfully!");
      }
      resetForm();
      loadPosts();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save blog post");
    }
    setLoading(false);
  };

  const handleDelete = async (slug) => {
    if (!window.confirm("Are you sure you want to delete this post?")) return;
    
    try {
      await api.deleteBlogPost(slug, password);
      toast.success("Blog post deleted successfully!");
      loadPosts();
    } catch (err) {
      toast.error("Failed to delete blog post");
    }
  };

  const generateSlug = (title) => {
    return title
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/(^-|-$)/g, "");
  };

  // Login Screen
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <Card className="w-full max-w-md border-0 shadow-xl">
          <CardHeader className="text-center pb-2">
            <div className="w-16 h-16 bg-brand-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
              <Lock className="w-8 h-8 text-brand-600" />
            </div>
            <CardTitle className="text-2xl font-bold text-gray-900">Admin Login</CardTitle>
            <p className="text-gray-500 text-sm mt-1">Enter password to manage blog posts</p>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleLogin} className="space-y-4">
              <div>
                <Label htmlFor="password" className="text-gray-700">Password</Label>
                <Input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter admin password"
                  className="mt-1.5 h-11 rounded-xl"
                  data-testid="admin-password-input"
                />
              </div>
              <Button 
                type="submit" 
                className="w-full bg-brand-600 hover:bg-brand-700 h-11 rounded-xl font-semibold"
                data-testid="admin-login-button"
              >
                Access Admin Panel
              </Button>
              <Link to="/blog" className="block">
                <Button variant="ghost" className="w-full text-gray-500 hover:text-brand-600">
                  <ArrowLeft className="w-4 h-4 mr-2" /> Back to Blog
                </Button>
              </Link>
            </form>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Admin Dashboard
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b sticky top-0 z-10">
        <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/blog">
              <Button variant="ghost" size="sm" className="text-gray-500">
                <ArrowLeft className="w-4 h-4 mr-1" /> Blog
              </Button>
            </Link>
            <h1 className="text-xl font-bold text-gray-900">Blog Admin</h1>
          </div>
          <Button 
            onClick={() => { resetForm(); setShowForm(true); }}
            className="bg-brand-600 hover:bg-brand-700 rounded-xl h-10"
            data-testid="create-post-button"
          >
            <Plus className="w-4 h-4 mr-2" /> New Post
          </Button>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 py-8">
        {/* Form Modal */}
        {showForm && (
          <Card className="mb-8 border-0 shadow-lg">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-lg">
                {editingPost ? "Edit Post" : "Create New Post"}
              </CardTitle>
              <Button variant="ghost" size="sm" onClick={resetForm}>
                <X className="w-4 h-4" />
              </Button>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-5">
                <div className="grid md:grid-cols-2 gap-5">
                  <div>
                    <Label className="text-gray-700">Title *</Label>
                    <Input
                      value={formData.title}
                      onChange={(e) => {
                        setFormData({ 
                          ...formData, 
                          title: e.target.value,
                          slug: editingPost ? formData.slug : generateSlug(e.target.value)
                        });
                      }}
                      placeholder="Enter post title"
                      className="mt-1.5 h-10 rounded-lg"
                      required
                      data-testid="post-title-input"
                    />
                  </div>
                  <div>
                    <Label className="text-gray-700">Slug *</Label>
                    <Input
                      value={formData.slug}
                      onChange={(e) => setFormData({ ...formData, slug: e.target.value })}
                      placeholder="url-friendly-slug"
                      className="mt-1.5 h-10 rounded-lg"
                      required
                      disabled={!!editingPost}
                      data-testid="post-slug-input"
                    />
                  </div>
                </div>

                <div className="grid md:grid-cols-3 gap-5">
                  <div>
                    <Label className="text-gray-700">Category *</Label>
                    <select
                      value={formData.category}
                      onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                      className="mt-1.5 w-full h-10 rounded-lg border border-gray-200 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
                      required
                      data-testid="post-category-select"
                    >
                      <option value="">Select category</option>
                      {categories.map(cat => (
                        <option key={cat} value={cat}>{cat}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <Label className="text-gray-700">Author *</Label>
                    <Input
                      value={formData.author}
                      onChange={(e) => setFormData({ ...formData, author: e.target.value })}
                      placeholder="Author name"
                      className="mt-1.5 h-10 rounded-lg"
                      required
                      data-testid="post-author-input"
                    />
                  </div>
                  <div>
                    <Label className="text-gray-700">Read Time *</Label>
                    <Input
                      value={formData.readTime}
                      onChange={(e) => setFormData({ ...formData, readTime: e.target.value })}
                      placeholder="e.g., 5 min read"
                      className="mt-1.5 h-10 rounded-lg"
                      required
                      data-testid="post-readtime-input"
                    />
                  </div>
                </div>

                <div>
                  <Label className="text-gray-700">Image URL *</Label>
                  <Input
                    value={formData.image}
                    onChange={(e) => setFormData({ ...formData, image: e.target.value })}
                    placeholder="https://images.unsplash.com/..."
                    className="mt-1.5 h-10 rounded-lg"
                    required
                    data-testid="post-image-input"
                  />
                </div>

                <div>
                  <Label className="text-gray-700">Excerpt *</Label>
                  <Textarea
                    value={formData.excerpt}
                    onChange={(e) => setFormData({ ...formData, excerpt: e.target.value })}
                    placeholder="Brief summary of the post (shown in cards)"
                    className="mt-1.5 rounded-lg resize-none"
                    rows={2}
                    required
                    data-testid="post-excerpt-input"
                  />
                </div>

                <div>
                  <Label className="text-gray-700">Content *</Label>
                  <Textarea
                    value={formData.content}
                    onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                    placeholder="Full blog post content. Use double line breaks for paragraphs."
                    className="mt-1.5 rounded-lg resize-none"
                    rows={8}
                    required
                    data-testid="post-content-input"
                  />
                </div>

                <div className="flex gap-3 pt-2">
                  <Button 
                    type="submit" 
                    className="bg-brand-600 hover:bg-brand-700 rounded-xl h-10 px-6"
                    disabled={loading}
                    data-testid="save-post-button"
                  >
                    <Save className="w-4 h-4 mr-2" />
                    {editingPost ? "Update Post" : "Create Post"}
                  </Button>
                  <Button type="button" variant="outline" onClick={resetForm} className="rounded-xl h-10">
                    Cancel
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        )}

        {/* Posts List */}
        <div className="grid gap-4">
          {loading && !posts.length ? (
            <div className="text-center py-12 text-gray-500">Loading...</div>
          ) : posts.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-gray-500 mb-4">No blog posts yet.</p>
              <Button onClick={() => setShowForm(true)} className="bg-brand-600 hover:bg-brand-700 rounded-xl">
                <Plus className="w-4 h-4 mr-2" /> Create Your First Post
              </Button>
            </div>
          ) : (
            posts.map((post) => (
              <Card key={post.id || post.slug} className="border-0 shadow-sm hover:shadow-md transition-shadow">
                <CardContent className="p-5">
                  <div className="flex items-start gap-4">
                    <img
                      src={post.image}
                      alt={post.title}
                      className="w-24 h-24 object-cover rounded-xl flex-shrink-0"
                    />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-4">
                        <div>
                          <Badge className="bg-brand-100 text-brand-700 text-xs mb-2">
                            {post.category}
                          </Badge>
                          <h3 className="font-semibold text-gray-900 text-lg leading-tight mb-1">
                            {post.title}
                          </h3>
                          <p className="text-gray-500 text-sm line-clamp-2">{post.excerpt}</p>
                          <div className="flex items-center gap-4 mt-2 text-xs text-gray-400">
                            <span>{post.author}</span>
                            <span>{post.date}</span>
                            <span>{post.readTime}</span>
                          </div>
                        </div>
                        <div className="flex items-center gap-2 flex-shrink-0">
                          <Link to={`/blog/${post.slug}`} target="_blank">
                            <Button variant="ghost" size="sm" className="text-gray-500 hover:text-brand-600">
                              <Eye className="w-4 h-4" />
                            </Button>
                          </Link>
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            onClick={() => handleEdit(post)}
                            className="text-gray-500 hover:text-brand-600"
                            data-testid={`edit-post-${post.slug}`}
                          >
                            <Pencil className="w-4 h-4" />
                          </Button>
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            onClick={() => handleDelete(post.slug)}
                            className="text-gray-500 hover:text-red-600"
                            data-testid={`delete-post-${post.slug}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))
          )}
        </div>
      </div>
    </div>
  );
}
